import React, { useState, useEffect } from "react";
import apiClient from "../api/ResturantClient";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const MAP_LIBRARIES = ["places"];

const UserProfile = () => {
  const user_id = parseInt(sessionStorage.getItem("user_id"));
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editableUser, setEditableUser] = useState({});
  const [autocomplete, setAutocomplete] = useState(null);
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await apiClient.get(`/auth/user/${user_id}`);
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user profile", error);
      }
    };

    fetchUser();
  }, [user_id]);

  const handleEdit = () => {
    setEditMode(true);
    setEditableUser(user);
  };

  const handleSave = async () => {
    try {
      await apiClient.put(`/auth/user/${user_id}`, editableUser);
      setUser(editableUser);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating user profile", error);
    }
  };

  const handleChange = (e) => {
    setEditableUser({ ...editableUser, [e.target.name]: e.target.value });
  };

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place) {
        const { formatted_address, geometry, address_components } = place;
        const addressDetails = {
          address: formatted_address || "",
          latitude: geometry?.location?.lat(),
          longitude: geometry?.location?.lng(),
          city: "",
          state: "",
          country: "",
          zip_code: "",
        };

        address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality"))
            addressDetails.city = component.long_name;
          if (types.includes("administrative_area_level_1"))
            addressDetails.state = component.long_name;
          if (types.includes("country"))
            addressDetails.country = component.long_name;
          if (types.includes("postal_code"))
            addressDetails.zip_code = component.long_name;
        });

        setEditableUser((prevData) => ({
          ...prevData,
          ...addressDetails,
        }));
      }
    }
  };

  const navigateToSubscription = () => {
    navigate("/subscription-details");
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-left">
          User Profile
        </h2>

        <div className="space-y-6">
          <div className="flex gap-4">
            {editMode ? (
              <button
                onClick={handleSave}
                className="flex-1 py-2 px-4 bg-[#abbfff] text-white rounded-lg hover:bg-[#4e7ab4] transition-colors"
              >
                Save Changes
              </button>
            ) : (
              <button
                onClick={handleEdit}
                className="flex-1 py-2 px-4 bg-[#abbfff] text-white rounded-lg hover:bg-[#4e7ab4] transition-colors"
              >
                Edit Profile
              </button>
            )}
            <button
              onClick={navigateToSubscription}
              className="flex-1 py-2 px-4 bg-[#4e7ab4] text-white rounded-lg hover:bg-[#3a5a8c] transition-colors"
            >
              Subscription Details
            </button>
          </div>

          <div className="grid gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                First Name
              </h3>
              {editMode ? (
                <input
                  name="first_name"
                  value={editableUser.first_name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.first_name}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Last Name
              </h3>
              {editMode ? (
                <input
                  name="last_name"
                  value={editableUser.last_name}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.last_name}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Address
              </h3>
              {editMode ? (
                <>
                  {isLoaded && (
                    <Autocomplete
                      onLoad={setAutocomplete}
                      onPlaceChanged={handlePlaceSelect}
                    >
                      <input
                        type="text"
                        placeholder="Search Address"
                        defaultValue={editableUser.address}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </Autocomplete>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Use the search above or manually edit the fields below
                  </p>
                </>
              ) : (
                <p className="text-gray-800 text-left">{user.address}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                City
              </h3>
              {editMode ? (
                <input
                  name="city"
                  value={editableUser.city}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.city}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                State
              </h3>
              {editMode ? (
                <input
                  name="state"
                  value={editableUser.state}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.state}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Country
              </h3>
              {editMode ? (
                <input
                  name="country"
                  value={editableUser.country}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.country}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Zip Code
              </h3>
              {editMode ? (
                <input
                  name="zip_code"
                  value={editableUser.zip_code}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-800 text-left">{user.zip_code}</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Email
              </h3>
              <p className="text-gray-800 text-left">{user.email}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1 text-left">
                Phone
              </h3>
              <p className="text-gray-800 text-left">{user.phone}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
